'use client';

import {
  SiFacebook,
  SiGithub,
  SiInstagram,
  SiX,
  SiYoutube,
} from '@icons-pack/react-simple-icons';
import {
  Button,
  CommandDialog,
  CommandEmpty,
  CommandFooter,
  CommandFooterTrigger,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Kbd,
} from '@mcdougald/ui/src';
import { CodeIcon, CommandIcon, LinkIcon } from 'lucide-react';
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  SITE_FACEBOOK_URL,
  SITE_GITHUB_URL,
  SITE_INSTAGRAM_URL,
  SITE_X_URL,
  SITE_YOUTUBE_URL,
} from '@/constants/website';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';

type Groups = Array<{
  name: string;
  actions: Array<{
    title: string;
    icon: ReactNode;
    onSelect: () => void | Promise<void>;
  }>;
}>;

/**
 * Search functionality in header
 */
const CommandMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectingValue, setSelectingValue] = useState('');
  const [copy] = useCopyToClipboard();
  const isSelectingCommand = ['Sign-out', 'Copy-link'].includes(selectingValue);
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen((value) => !value);
      }
    };

    globalThis.document.addEventListener('keydown', down);

    return () => globalThis.document.removeEventListener('keydown', down);
  }, []);

  const openLink = useCallback((url: string) => {
    setIsOpen(false);
    window.open(url, '_blank', 'noopener');
  }, []);

  const groups: Groups = [
    // {
    //   name: 'Account', // t('command-menu.groups.account'),
    //   actions: [
    //     // biome-ignore lint/correctness/noConstantCondition: <explanation>
    //     ...(false
    //       ? [
    //           {
    //             title: 'Sign-out',
    //             icon: <LogOutIcon className="mr-3 size-4" />,
    //             onSelect: async () => {
    //               // await signOut({
    //               //   fetchOptions: {
    //               //     onSuccess: () => {
    //               //       router.refresh();
    //               //     },
    //               //   },
    //               // });
    //             },
    //           }
    //         ]
    //       : [
    //           {
    //             title: 'Sign-in', // t('common.sign-in'),
    //             icon: <LogInIcon className="mr-3 size-4" />,
    //             onSelect: () => {
    //               setIsOpen(false);
    //               setIsSignInOpen(true);
    //             },
    //           },
    //         ]),
    //   ],
    // },
    {
      name: 'Commands', // t('command-menu.groups.general'),
      actions: [
        {
          title: 'Copy-link', //t('command-menu.actions.copy-link'),
          icon: <LinkIcon className="mr-3 size-4" />,
          onSelect: async () => {
            setIsOpen(false);

            await copy({ text: globalThis.location.href });
          },
        },
        {
          title: 'Copy-code', //t('command-menu.actions.source-code'),
          icon: <CodeIcon className="mr-3 size-4" />,
          onSelect: () => openLink('https://github.com/mcdougald/mcdougald'),
        },
      ],
    },
    {
      name: 'Social', //t('command-menu.groups.social'),
      actions: [
        {
          title: 'GitHub',
          icon: <SiGithub className="mr-3 size-4" />,
          onSelect: () => openLink(SITE_GITHUB_URL),
        },
        {
          title: 'Facebook',
          icon: <SiFacebook className="mr-3 size-4" />,
          onSelect: () => openLink(SITE_FACEBOOK_URL),
        },
        {
          title: 'Instagram',
          icon: <SiInstagram className="mr-3 size-4" />,
          onSelect: () => openLink(SITE_INSTAGRAM_URL),
        },
        {
          title: 'X',
          icon: <SiX className="mr-3 size-4" />,
          onSelect: () => openLink(SITE_X_URL),
        },
        {
          title: 'YouTube',
          icon: <SiYoutube className="mr-3 size-4" />,
          onSelect: () => openLink(SITE_YOUTUBE_URL),
        },
      ],
    },
  ];

  return (
    <>
      <Button
        variant="ghost"
        className="size-9 p-0"
        onClick={() => setIsOpen(true)}
        id="Command-Menu-Button"
        data-umami-event="Command-Menu-Button"
        // aria-label={t('command-menu.open-menu')}
      >
        <CommandIcon className="command-icon size-4 text-gray-700" />
      </Button>
      <CommandDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        value={selectingValue}
        onValueChange={setSelectingValue}
      >
        <CommandInput
          placeholder={'Search'}
          className={'text-gray-700'}
          // placeholder={t('command-menu.placeholder')}
        />
        <CommandList>
          <CommandEmpty >{'No Results'}</CommandEmpty>
          {groups.map((group, i) => (
            <Fragment key={group.name}>
              <CommandGroup
                heading={group.name}
                className={
                  '[.aria-selected.bg-accent_&]:!bg-white/80'
                }
              >
                {group.actions.map((action) => (
                  <CommandItem
                    key={action.title}
                    onSelect={action.onSelect}
                    className={
                      'text-gray-700 hover:!bg-white/80 active:!bg-white/80'
                    }
                  >
                    {action.icon}
                    {action.title}
                  </CommandItem>
                ))}
              </CommandGroup>
              {i === groups.length - 1 ? null : <CommandSeparator />}
            </Fragment>
          ))}
        </CommandList>
        <CommandFooter>
          {/*<Logo className="size-4" />*/}
          <CommandFooterTrigger
            triggerKey={<Kbd keys={['enter']} className="py-0 text-white" />}
            className={'text-gray-700'}
          >
            {isSelectingCommand ? 'Open Command' : 'Open Link'}
          </CommandFooterTrigger>
        </CommandFooter>
      </CommandDialog>
    </>
  );
};

export default CommandMenu;
